import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { FileTypeEnum } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import UploadFileInput from '@repo/ui/file-input'
import { UploadIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import MultiSelectInput from '@repo/ui/multiselect-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { printFormat } from 'iban-ts'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z, ZodType } from 'zod'

import { getCenterOlPaymentSuggestions } from '../../../api/center-ol-payments/get-center-ol-payment-suggestions'
import { getCenters } from '../../../api/centers/get-centers'
import { getCountries } from '../../../api/countries/get-countries'
import { getLecBankAccounts } from '../../../api/lec-bank-accounts/get-lec-bank-accounts'
import { getOls } from '../../../api/ols/get-ols'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  CenterOlPaymentTypeEnum,
  centerOlPaymentTypeMapping
} from '../../../lib/types/enums/center-ol-payment/center-ol-payment-type.enum'
import { CurrencyEnum, currencyEnumMapping } from '../../../lib/types/enums/common/currency.enum'
import { CenterOlPaymentInput } from '../../../lib/types/models/center-ol-payment/center-ol-payment-input'
import { PeriodTypes } from '../../../lib/types/models/period/period'
import { BaseFormProps, NullableTransformer } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { getLocalizedLabel } from '../../../utils/functions/get-localized-label'
import { getOlName } from '../../../views/ols/center/utils/functions'
import CountryFlag from '../../elements/country-flag'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import { ControlledCurrencyInput } from '../../elements/inputs/controlled-currency-input'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<CenterOlPaymentInput, true> & {
  mode: 'default' | 'view'
  periods?: PeriodTypes.Full[] // the saved périodes of the payment being edited
}

export const centerOlPaymentFormSchema: ZodType<NullableTransformer<CenterOlPaymentInput>> = z
  .object({
    type: createEnumSchema({ enumType: CenterOlPaymentTypeEnum, fieldName: 'Type de virement' }),
    label: createStringSchema('Libellé'),
    additionalLabel: createStringSchema('Libellé supplémentaire', { nullish: true }),
    dateOfPayment: createStringSchema('Date de paiement'),
    currency: createEnumSchema({ enumType: CurrencyEnum, fieldName: 'Devise' }),
    amount: createNumberSchema('Montant'),
    olId: createNumberSchema('OL'),
    centerId: createNumberSchema('Centre'),
    lecBankAccountId: createNumberSchema('Compte bancaire LEC'),
    countryId: createNumberSchema('Pays', { optional: true }),
    olBankAccountId: createNumberSchema('Compte bancaire OL', { nullish: true }),
    files: FILE_VALIDATION().array().default([])
  })
  .superRefine((values, ctx) => {
    if (values.currency === CurrencyEnum.USD) {
      if (values.olBankAccountId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['olBankAccountId'],
          message: 'Compte bancaire OL doit être vide pour les paiements en USD'
        })
      }

      return
    }

    if (!values.olBankAccountId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['olBankAccountId'],
        message: 'Compte bancaire OL obligatoire'
      })
    }
  })

export const CenterOlPaymentForm = ({
  mode,
  defaultValues,
  periods,
  ...validationButtonProps
}: Props) => {
  const { currentLanguage } = useTranslate()
  const form = useForm<CenterOlPaymentInput>({
    resolver: zodResolver(centerOlPaymentFormSchema),
    defaultValues,
    mode: FORM_MODE
  })
  const { olId, centerId, type, countryId, currency, amount, olBankAccountId, lecBankAccountId } =
    form.watch()

  const { data: ols } = useQuery({
    queryKey: ['ols'],
    queryFn: () => getOls()
  })

  const { data: centers } = useQuery({
    queryKey: ['centers', 'BASIC'],
    queryFn: () => getCenters({ version: 'BASIC' })
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: suggestions, isLoading } = useQuery({
    queryKey: ['center-bank-payment-suggestions', olId, centerId, type],
    queryFn: () => getCenterOlPaymentSuggestions({ olId, centerId, type }),
    enabled: !!olId && mode !== 'view'
  })

  const selectedOl = ols?.data.find((ol) => ol.id === olId)

  const olBankAccountOptions = selectedOl?.bankAccounts
  const isUsdPayment = currency === CurrencyEnum.USD

  const { data: lecBankAccounts } = useQuery({
    queryKey: ['center-bank-payment-suggestions'],
    queryFn: () => getLecBankAccounts(),
    enabled: !!olId
  })

  useEffect(() => {
    // when the OL changes, we need to reset the selected inputs that are related to it.
    form.resetField('centerId', undefined)
    form.resetField('countryId', undefined)
    form.resetField('label', undefined)
    form.resetField('currency', undefined)
    form.resetField('amount', undefined)
    form.resetField('lecBankAccountId', undefined)
    form.resetField('olBankAccountId', undefined)
  }, [olId])

  useEffect(() => {
    // when the center changes, we need to reset the selected inputs that are related to it.
    form.resetField('countryId', undefined)
    form.resetField('label', undefined)
    form.resetField('amount', undefined)
  }, [centerId])

  useEffect(() => {
    // when the payment type changes, reset fields derived from the payment suggestions.
    if (defaultValues?.centerId) return

    form.resetField('label', undefined)
    form.resetField('amount', undefined)
  }, [type])

  useEffect(() => {
    if (!suggestions || defaultValues?.centerId) return // edit mode should keep the loaded payment values

    if (centerId && suggestions.countryId && !countryId) {
      form.setValue('countryId', suggestions.countryId, { shouldDirty: true })
      form.trigger('countryId') // to trigger validation and update buttons state
    }

    if (!lecBankAccountId && suggestions.lecBankAccountId) {
      form.setValue('lecBankAccountId', suggestions.lecBankAccountId, { shouldDirty: true })
      form.trigger('lecBankAccountId') // to trigger validation and update buttons state
    }

    if (centerId && suggestions.label) {
      form.setValue('label', suggestions.label, { shouldDirty: true })
      form.trigger('label') // to trigger validation and update buttons state
    }

    if (!currency && suggestions.currency) {
      form.setValue('currency', suggestions.currency, { shouldDirty: true })
      form.trigger('currency') // to trigger validation and update buttons state
    }

    if (!amount && suggestions.amount) {
      form.setValue('amount', suggestions.amount, { shouldDirty: true })
      form.trigger('amount') // to trigger validation and update buttons state
    }
  }, [
    suggestions,
    centerId,
    countryId,
    currency,
    amount,
    lecBankAccountId,
    defaultValues?.centerId
  ])

  useEffect(() => {
    if (currency === CurrencyEnum.USD && olBankAccountId) {
      form.setValue('olBankAccountId', null, { shouldDirty: true })
      form.trigger('olBankAccountId')
    }
  }, [currency, olBankAccountId])

  useEffect(() => {
    if (
      !selectedOl ||
      !olBankAccountOptions ||
      olBankAccountOptions.length === 0 ||
      currency === CurrencyEnum.USD ||
      selectedOl.currency === CurrencyEnum.USD ||
      (!olBankAccountId && !!defaultValues?.olBankAccountId)
    )
      return

    const suggestedOlBankAccountId = olBankAccountOptions.find(
      (account) => account.currency === selectedOl.currency
    )?.id

    // set the first OL bank account as default when the OL changes and there is no selected bank account
    if (suggestedOlBankAccountId) {
      form.setValue('olBankAccountId', suggestedOlBankAccountId, { shouldDirty: true })
    }
  }, [currency, olBankAccountOptions, olBankAccountId, selectedOl])

  // the périodes are not editable: the backend always saves the ones of the center's stay dates,
  // shown from the suggestions once a center is picked, else the saved ones of the unchanged center
  const periodOptions = useMemo(
    () =>
      centerId && (suggestions?.periods || centerId !== defaultValues?.centerId)
        ? (suggestions?.periods ?? [])
        : (periods ?? []).map((period) => ({
            label: getLocalizedLabel(period, currentLanguage, { fr: 'name', en: 'nameEn' }),
            value: period.id
          })),
    [centerId, suggestions?.periods, defaultValues?.centerId, periods, currentLanguage]
  )

  const disabled = mode === 'view'

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox className={cn('p-4 rounded-2xl')}>
          <div className="grid grid-cols-3 gap-3">
            <FormField
              control={form.control}
              name={'olId'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput
                      required
                      {...field}
                      searchable
                      label={'Prestataire'}
                      disabled={disabled}
                      options={ols?.data.map((ol) => ({
                        label: getOlName(ol, { type: ol.type }),
                        value: ol.id
                      }))}
                      selectedOption={getSelectedOption(
                        field.value,
                        getOlName(ols?.data.find((ol) => ol.id === field.value))
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'centerId'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput
                      required
                      {...field}
                      searchable
                      label={'Centre'}
                      options={suggestions?.centers}
                      disabled={isLoading || !suggestions?.centers?.length || disabled}
                      selectedOption={getSelectedOption(
                        field.value,
                        suggestions?.centers?.find((center) => center.value === field.value)
                          ?.label ??
                          centers?.data.find((center) => center.id === field.value)?.name
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'type'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput<CenterOlPaymentTypeEnum>
                      required
                      {...field}
                      label={'Type de virement'}
                      disabled={disabled}
                      options={Object.entries(centerOlPaymentTypeMapping).map(
                        ([value, { label }]) => ({
                          value: value as CenterOlPaymentTypeEnum,
                          label
                        })
                      )}
                      selectedOption={
                        field.value
                          ? {
                              value: field.value,
                              label: centerOlPaymentTypeMapping[field.value].label
                            }
                          : undefined
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 mt-2">
            <FormField
              control={form.control}
              name={'countryId'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      label={'Pays'}
                      disabled={isLoading || disabled}
                      options={countries?.data.map(({ id, name }) => ({
                        label: name,
                        value: id,
                        custom: <CountryFlag possibleCountry={name} label={name} />
                      }))}
                      selectedOption={
                        field?.value
                          ? {
                              label: countries?.data.find((c) => c.id === field.value)?.name ?? '',
                              value: field.value
                            }
                          : undefined
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <MultiSelectInput<number>
              variant="orange"
              label={'Période(s)'}
              disabled
              options={periodOptions}
              selectedOptions={periodOptions}
            />

            <FormField
              control={form.control}
              name={'label'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      required
                      {...field}
                      type="text"
                      label={'Libellé'}
                      disabled={disabled}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'additionalLabel'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type="text"
                      label={'Complément libellé'}
                      disabled={disabled}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'amount'}
              render={() => (
                <FormItem>
                  <FormControl>
                    <ControlledCurrencyInput
                      disabled={disabled}
                      value={
                        currency || amount
                          ? { value: amount ? String(amount) : '', selectedOptionValue: currency }
                          : undefined
                      }
                      onChange={(value) => {
                        if (value.selectedOptionValue) {
                          const amount = value.value ? +value.value : null
                          if (amount !== null && !isNaN(amount))
                            form.setValue('amount', amount, { shouldDirty: true })

                          form.setValue('currency', value.selectedOptionValue, {
                            shouldDirty: true
                          })
                        }
                        form.trigger(['amount', 'currency']) // to trigger validation and update buttons state
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'lecBankAccountId'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput
                      required
                      {...field}
                      label={'Depuis compte LEC'}
                      disabled={isLoading || !lecBankAccounts?.data?.length || disabled}
                      options={
                        lecBankAccounts?.data.map((account) => ({
                          label: `${account.bankName} (${account.accountHolder})`,
                          value: account.id
                        })) || []
                      }
                      selectedOption={
                        field.value
                          ? lecBankAccounts?.data
                              .map((account) => ({
                                label: `${account.bankName} (${account.accountHolder})`,
                                value: account.id
                              }))
                              .find((option) => option.value === field.value)
                          : undefined
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'files'}
              render={({ field, fieldState }) => {
                return (
                  <FormItem className={'col-span-2'}>
                    <FormControl>
                      <UploadFileInput
                        multiple
                        previewOnClick
                        showDeleteIcon={!disabled}
                        icon={UploadIcon}
                        label={'Facture(s)'}
                        files={field?.value}
                        disabled={disabled}
                        types={[FileTypeEnum.PDF]}
                        hasError={!!fieldState.error}
                        placeholder={'Glisser-déposer ou parcourir'}
                        onFilesChange={field.onChange}
                        onFileError={(error) => {
                          if (error) toast.error(error)
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <Typography variant="tag">Compte destinataire</Typography>
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name={'olBankAccountId'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledSelectInput
                        required={!isUsdPayment}
                        {...field}
                        label={'IBAN Number'}
                        disabled={isUsdPayment || !olBankAccountOptions?.length || disabled}
                        options={
                          olBankAccountOptions
                            ? olBankAccountOptions.map((account) => ({
                                label: printFormat(account.iban),
                                value: account.id
                              }))
                            : []
                        }
                        selectedOption={
                          field.value
                            ? olBankAccountOptions
                                ?.map((account) => ({
                                  label: printFormat(account.iban),
                                  value: account.id
                                }))
                                .find((option) => option.value === field.value)
                            : undefined
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'dateOfPayment'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        required
                        disabledPastDates
                        {...field}
                        disabled={disabled}
                        mode="dynamic"
                        label={'Date du virement'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="col-span-2 flex justify-end items-center">
                <Badge
                  size="xsmall"
                  text={`Devise compte : ${currency ? currencyEnumMapping[currency].label : '-'}`}
                />
              </div>
            </div>
          </div>
        </InnerBox>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}
